import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ChevronUp } from 'lucide-react';
import hotelService from '../services/hotelService';
import { getOffMarketStayById } from '../services/offMarketStaysService';

// Import components
import MainLayout from '../components/Layout/MainLayout';
import HotelHero from '../components/hotel/HotelHero';
import HotelImageGallery from '../components/hotel/HotelImageGallery';
import HotelSearchBar from '../components/hotel/HotelSearchBar';
import RoomExplorerSelection from '../components/hotel/RoomExplorerSelection';
import AboutHotel from '../components/hotel/AboutHotel';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorBoundary from '../components/ErrorBoundary';
import OffMarketStayBanner from '../components/OffMarketStayBanner';

const HotelDetailsPage = () => {
  const { hotelCode } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // State management
  const [hotelData, setHotelData] = useState(null);
  const [roomsData, setRoomsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [showOffMarketBanner, setShowOffMarketBanner] = useState(false);
  const [offMarketProperty, setOffMarketProperty] = useState(null);

  // Search parameters from URL
  const searchCriteria = useMemo(() => {
    const checkInDate = searchParams.get('checkInDate');
    const checkOutDate = searchParams.get('checkOutDate');
    const adults = parseInt(searchParams.get('adults')) || 2;
    const children = parseInt(searchParams.get('children')) || 0;
    const childAges = searchParams.get('childAges') || '';
    const currency = searchParams.get('currency') || 'USD';
    /** From off-market stays list: `offMarketStay=1` + Mongo id of off-market property */
    const offMarketStayRaw = searchParams.get('offMarketStay');
    const offMarketStay =
      offMarketStayRaw === '1' ||
      String(offMarketStayRaw || '').toLowerCase() === 'true';
    const offMarketPropertyId = searchParams.get('offMarketPropertyId');

    return {
      checkInDate,
      checkOutDate,
      adults,
      children,
      childAges,
      currency,
      offMarketStay,
      offMarketPropertyId,
    };
  }, [searchParams]);

  // Validate required search parameters
  const isValidSearch = useMemo(() => {
    return (
      hotelCode &&
      searchCriteria.checkInDate &&
      searchCriteria.checkOutDate &&
      new Date(searchCriteria.checkInDate) < new Date(searchCriteria.checkOutDate)
    );
  }, [hotelCode, searchCriteria]);

  // Fetch hotel details
  const fetchHotelDetails = async () => {
    if (!isValidSearch) {
      setError('Invalid search parameters. Please check dates and hotel code.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        checkInDate: searchCriteria.checkInDate,
        checkOutDate: searchCriteria.checkOutDate,
        adults: searchCriteria.adults.toString(),
        children: searchCriteria.children.toString(),
        currency: searchCriteria.currency,
        ...(searchCriteria.childAges && { childAges: searchCriteria.childAges })
      });

      // Use the new service method that properly extracts hotel names
      const response = await hotelService.getHotelDetailsForPage(hotelCode);


      if (response.status === 'success' || response.success) {
        setHotelData(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch hotel details');
      }
    } catch (err) {
      console.error('Error fetching hotel details:', err);
      setError(err.message || 'Failed to load hotel details');
      toast.error('Failed to load hotel details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available rooms
  const fetchAvailableRooms = async () => {
    if (!isValidSearch) return;

    try {
      setRoomsLoading(true);

      const params = new URLSearchParams({
        checkInDate: searchCriteria.checkInDate,
        checkOutDate: searchCriteria.checkOutDate,
        adults: searchCriteria.adults.toString(),
        children: searchCriteria.children.toString(),
        currency: searchCriteria.currency,
        ...(searchCriteria.childAges && { childAges: searchCriteria.childAges }),
        ...(searchCriteria.offMarketStay && {
          offMarketStay: '1',
          rateSource: '100'
        })
      });


      const response = await hotelService.getAvailableRoomsForHotelDetails(
        hotelCode,
        params.toString()
      );


      if (response.status === 'success' || response.success) {
        setRoomsData(response.data);
      } else {
      }
    } catch (err) {
      // Don't show error toast for rooms - it's not critical
    } finally {
      setRoomsLoading(false);
    }
  };

  // Handle search parameter updates
  const handleSearchUpdate = (newSearchParams) => {
    const updatedParams = new URLSearchParams(searchParams);

    Object.entries(newSearchParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });

    setSearchParams(updatedParams);
  };

  // Handle room selection
  const handleRoomSelection = (selectionData) => {
    console.log('Room selected:', selectionData);
    // Here you can navigate to booking page or show booking modal
    // For now, we'll just show a success message
    // toast.success(`Selected ${selectionData.roomType} - ${selectionData.ratePlan.ratePlanName}`);

    // You could navigate to booking page with the selection data
    // navigate('/booking', { state: { selection: selectionData } });
  };

  // Effect to fetch data when dependencies change
  useEffect(() => {
    fetchHotelDetails();
    fetchAvailableRooms();
  }, [hotelCode, searchParams]);

  // Show off-market banner when flagged and fetch property context data
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!searchCriteria.offMarketStay || !searchCriteria.offMarketPropertyId) {
        setShowOffMarketBanner(false);
        setOffMarketProperty(null);
        return;
      }
      setShowOffMarketBanner(true);
      try {
        const res = await getOffMarketStayById(searchCriteria.offMarketPropertyId);
        if (!cancelled && res?.success && res.data) {
          setOffMarketProperty(res.data);
        }
      } catch {
        if (!cancelled) setOffMarketProperty(null);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [searchCriteria.offMarketStay, searchCriteria.offMarketPropertyId]);

  // Loading state
  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-white">
          <div className="container mx-auto px-4 py-8">
            <LoadingSpinner message="Loading hotel details..." />
          </div>
        </div>
      </MainLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <MainLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8 pt-32">
          <div className="text-center">
            <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
              <h2 className="text-xl text-neutral-900 mb-4 typography-subsection-title">
                Unable to Load Hotel Details
              </h2>
              <p className="text-neutral-600 font-poppinsLight mb-6">{error}</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-stretch sm:items-center">
                <button
                  onClick={() => navigate('/hotels')}
                  className="bg-neutral-900 text-white px-6 py-2 rounded-lg hover:bg-black transition-colors duration-200 font-poppinsMedium w-full sm:w-auto"
                >
                  Back to Hotels
                </button>
                <button
                  onClick={fetchHotelDetails}
                  className="border border-neutral-300 text-neutral-700 px-6 py-2 rounded-lg hover:bg-neutral-50 transition-colors duration-200 font-poppinsMedium w-full sm:w-auto"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      </MainLayout>
    );
  }

  // Main render
  return (
    <MainLayout>
    <ErrorBoundary>
      {/* Off-market exclusive banner — shown when arriving from the Off-Market Stays list */}
      {showOffMarketBanner && (
        <OffMarketStayBanner
          offMarketPropertyId={searchCriteria.offMarketPropertyId}
          propertyData={offMarketProperty}
          onClose={() => setShowOffMarketBanner(false)}
        />
      )}
      <div className="min-h-screen bg-gray-50 pt-0 overflow-x-hidden">
          {/* Main Content */}
          <div className="space-y-0">
            {/* Hotel Hero Section */}
            <HotelHero
              hotelData={hotelData}
              hotelMedia={hotelData?.media}
              hotelContent={hotelData?.content}
              hotelDescriptive={hotelData?.descriptive}
              contentData={hotelData}
              mediaData={hotelData?.media}
              isOffMarketStay={Boolean(searchCriteria.offMarketStay)}
              offMarketProperty={offMarketProperty}
            />

          {/* Hotel Search Bar */}
          <div className="bg-white border-b border-gray-200">
            <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 max-w-full">
              <HotelSearchBar
                searchCriteria={searchCriteria}
                onSearchUpdate={handleSearchUpdate}
                hotelData={hotelData}
              />
            </div>
          </div>

          {/* Room Explorer Selection Section */}
          <div className="bg-gray-50">
            <div className="container mx-auto px-4 py-6">
              <RoomExplorerSelection
                hotelCode={hotelCode}
                roomsData={roomsData}
                loading={roomsLoading}
                searchCriteria={searchCriteria}
                hotelData={hotelData}
                onRoomSelected={handleRoomSelection}
                offMarketProperty={offMarketProperty}
              />
            </div>
          </div>

          {/* Hotel Image Gallery Section */}
          {hotelData?.images && hotelData.images.length > 0 && (
            <div className="bg-white">
              <div className="container mx-auto px-3 sm:px-4 py-8 sm:py-12 max-w-full">
                <div className="text-center mb-6 sm:mb-8">
                  <h2 className="text-2xl sm:text-3xl text-neutral-900 mb-3 sm:mb-4 typography-section-title px-1">Hotel Gallery</h2>
                  <p className="text-neutral-600 max-w-2xl mx-auto font-poppinsLight text-sm sm:text-base px-2">
                    Explore our collection of high-quality images showcasing the beauty and amenities of our hotel.
                  </p>
                </div>
                <HotelImageGallery
                  images={hotelData.images}
                  hotelName={hotelData?.hotelInfo?.name || 'Hotel'}
                />
              </div>
            </div>
          )}

          {/* Hotel About Section */}
          <div className="bg-white">
            <div className="container mx-auto px-3 sm:px-4 py-5 sm:py-6 max-w-full">
               <AboutHotel
                hotelData={hotelData}
                hotelContent={hotelData?.content}
                hotelDescriptive={hotelData?.descriptive}
                isOffMarketStay={Boolean(searchCriteria.offMarketStay)}
                offMarketProperty={offMarketProperty}
              />
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
    </MainLayout>
  );
};

export default HotelDetailsPage;
